import React, { FC, useContext, useEffect, useState } from 'react';
import { FaPen, FaSave } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Button from '../../../Components/Button';
import FormRow from '../../../Components/FormRow';
import Label from '../../../Components/Label';
import Password from '../../../Components/Password';
import SecurityQuestions from '../../../Components/SecurityQuestions';
import { LoadingSpinner } from '@saif/loading-spinner';
import UsernameInput from '../../../Components/UsernameInput';
import DataContext from '../../../Context';
import useSaifFetch from '../../../hooks/useSaifFetch';
import useWarnRefresh from '../../../hooks/useWarnRefresh';
import LocalizationContext from '../../../Localization/LanguageContext';
import { ILanguage } from '../../../Models/LocInfo';
import { IPolicy } from '../../../Models/PolicyInfo';
import { API_ACTION, SaifFetch } from '../../../Utils/SaifFetch';
import esSeqQuest from '../../../Localization/LanguageFiles/esSeqQuest.json';
import { ILocalizationContext } from '../../../Localization/Models/ILocalizationContext';

interface Props {
  edit: boolean;
  setEdit: React.Dispatch<React.SetStateAction<boolean>>;
  isLoginValid: boolean;
  setIsLoginValid: React.Dispatch<React.SetStateAction<boolean>>;
  setShowBanner: React.Dispatch<React.SetStateAction<boolean>>;
}

const ReviewCredentials: FC<Props> = ({
  edit,
  setEdit,
  isLoginValid,
  setIsLoginValid,
  setShowBanner,
}) => {
  const {
    username,
    setUsername,
    usernameValid,
    setUsernameValid,
    password,
    firstName,
    lastName,
    email,
    question1,
    question1Answer,
    question2,
    question2Answer,
    question3,
    question3Answer,
    setErrorPageMsg,
  } = useContext(DataContext) as IPolicy;

  const { locale } = useContext(LocalizationContext) as ILanguage;
  const [esQuestions] = useState<any>(esSeqQuest);
  const { getValue } = useContext(LocalizationContext) as ILocalizationContext;

  let navigator = useNavigate();
  useWarnRefresh();

  const [body, setBody] = useState({});
  const { data, loading, status, error } = useSaifFetch<any>(
    API_ACTION.Validate_Username,
    body,
    SaifFetch.post
  );

  // Use for client side validation in the future;
  const [isValid, setIsValid] = useState(true);
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [securityQuestionsValid, setSecurityQuestionsValid] = useState(true);
  const [required, setRequired] = useState(false);

  const hidePassword = (password: string) => {
    let result = '';
    for (let i = 0; i < password.length; i++) {
      result += '*';
    }
    return result;
  };

  useEffect(() => {
    if (status === 200) {
      setUsernameValid((prev) => {
        return {
          invalid: false,
          isShort: prev.isShort,
          touched: true,
          empty: prev.empty,
          nonLegitChars: prev.nonLegitChars,
        };
      });
      setEdit(false);
    } else if (status === 400) {
      let invalidUsername = false;
      if ('username' in data) {
        invalidUsername = true;
      }
      setUsernameValid((prev) => {
        return {
          invalid: invalidUsername,
          isShort: prev.isShort,
          touched: true,
          empty: prev.empty,
          nonLegitChars: prev.nonLegitChars,
        };
      });
    } else if ((status && status >= 401) || error) {
      setErrorPageMsg(getValue('Error', 'ErrorDetail'));
      navigator('/error', { replace: true });
    }
  }, [status]);

  useEffect(() => {
    if (
      isPasswordValid &&
      securityQuestionsValid &&
      username.length > 7 &&
      usernameMatchValid()
    ) {
      setIsLoginValid(true);
    } else {
      setIsLoginValid(false);
    }
  }, [isValid, isPasswordValid, securityQuestionsValid, username]);

  const UserNameisValid = () => {
    return (
      usernameValid.nonLegitChars === false &&
      usernameValid.isShort === false &&
      usernameValid.touched === true &&
      usernameValid.invalid === false
    );
  };

  const onSave = () => {
    setShowBanner(false);
    if (isLoginValid) setBody({ username: username, password: password });
  };

  const usernameMatchValid = () => {
    return !(
      password.toLowerCase().includes((username as string).toLowerCase()) ||
      (password.toLowerCase().includes((firstName as string).toLowerCase()) &&
        (firstName as string).length >= 2) ||
      (password.toLowerCase().includes((lastName as string).toLowerCase()) &&
        (lastName as string).length >= 2) ||
      password
        .toLowerCase()
        .includes(
          (email as string)
            .toLowerCase()
            .substring(0, (email as string).indexOf('@'))
        )
    );
  };

  const ResetUserExists = () => {
    usernameValid.invalid = false;
  };

  return (
    <form className='policy-info-form position-relative'>
      {loading && (
          <LoadingSpinner className='loading' text={getValue('Review', 'UsernameCheck')} />
      )}
      <FormRow>
        <div className='edit-save-row'>
          <div className='left-side-title'>
            <h3 className='subtitle-left font-header-medium'>
              {getValue('Review', 'LoginHeading')}
            </h3>
          </div>
          <div className='right-side-title'>
            <div className='button-spacer' />

            {edit ? (
              <div className='tab button-icon'>
                <Button
                  type='button'
                  version={
                    isLoginValid && UserNameisValid() ? 'primary' : 'disabled'
                  }
                  bootstrap='col-2'
                  onClick={() => onSave()}
                  className='subtitle-right'
                >
                  <FaSave className='save-icon' />
                  &nbsp;&nbsp;{getValue('Review', 'Save')}
                </Button>
              </div>
            ) : (
              <div className='tab button-icon'>
                <Button
                  type='button'
                  version='secondary'
                  bootstrap='col-2'
                  onClick={() => setEdit(true)}
                  className='subtitle-right'
                >
                  <FaPen className='edit-icon' />
                  &nbsp;&nbsp;&nbsp;{getValue('Review', 'Edit')}
                </Button>
              </div>
            )}
          </div>
        </div>
      </FormRow>
      <FormRow>
        <Label>{getValue('Review', 'UsernameLabel')}</Label>
        {edit ? (
          <>
            <small className='username-label text-xs pb-1'>
              {getValue('Review', 'UsernameLabelSub')}
            </small>
            <UsernameInput
              inputId='Username'
              inputValue={username}
              type='text'
              isValid={usernameValid}
              errorMessage={getValue('Review', 'ValUserRequired')}
              onChange={setUsername}
              setUsernameValid={setUsernameValid}
              maxLength={30}
              onFocus={ResetUserExists}
            />
          </>
        ) : (
          <p className='text-xs text-slate mb-2 px-0'>{username}</p>
        )}
      </FormRow>
      {edit ? (
        <Password
          setPasswordIsValid={setIsPasswordValid}
          required={required}
          customValidation={username.length > 7 && usernameMatchValid()}
        />
      ) : (
        <FormRow>
          <Label>{getValue('Password', 'PasswordLabel')}</Label>
          <p className='text-xs text-slate mb-2 px-0'>
            {hidePassword(password)}
          </p>
        </FormRow>
      )}
      {edit ? (
        <SecurityQuestions
          setSecurityQuestionsAreValid={setSecurityQuestionsValid}
          required={required}
        />
      ) : (
        <>
          <FormRow>
            <Label>{getValue('Review', 'SecQuest1Label')}</Label>
            <p className='text-xs text-slate mb-2 px-0'>
              {locale === 'es' && esQuestions['SpanishQuestions'][question1.id]
                ? esQuestions['SpanishQuestions'][question1.id]
                : question1.questionText}
            </p>
            <Label>{getValue('SecurityQuestions', 'Answer')}</Label>
            <p className='text-xs text-slate mb-2 px-0'>{question1Answer}</p>
          </FormRow>
          <FormRow>
            <Label>{getValue('Review', 'SecQuest2Label')}</Label>
            <p className='text-xs text-slate mb-2 px-0'>
              {locale === 'es' && esQuestions['SpanishQuestions'][question2.id]
                ? esQuestions['SpanishQuestions'][question2.id]
                : question2.questionText}
            </p>
            <Label>{getValue('SecurityQuestions', 'Answer')}</Label>
            <p className='text-xs text-slate mb-2 px-0'>{question2Answer}</p>
          </FormRow>
          <FormRow>
            <Label>{getValue('Review', 'SecQuest3Label')}</Label>
            <p className='text-xs text-slate mb-2 px-0'>
              {locale === 'es' && esQuestions['SpanishQuestions'][question3.id]
                ? esQuestions['SpanishQuestions'][question3.id]
                : question3.questionText}
            </p>
            <Label>{getValue('SecurityQuestions', 'Answer')}</Label>
            <p className='text-xs text-slate mb-2 px-0'>{question3Answer}</p>
          </FormRow>
        </>
      )}
      <div className='base-row'>
        <hr className='line-style' />
      </div>
    </form>
  );
};

export default ReviewCredentials;
